import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  Plus, FileEdit, ClipboardCheck, Target, Users
} from 'lucide-react';
import './QuickActions.css';

interface QuickAction {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  variant?: 'primary' | 'warning' | 'info' | 'success' | 'accent';
  roles: string[];
}

interface QuickActionsProps {
  pendingReviewCount?: number;
  myDraftCount?: number;
  activeMissionCount?: number;
}

function QuickActions({ pendingReviewCount = 0, myDraftCount = 0, activeMissionCount = 0 }: QuickActionsProps) {
  const { t } = useTranslation();
  const { user, isReadOnlyInstance } = useAuth();

  const actions: QuickAction[] = [];

  // NATION / EU - New Request (EXCON is not a nation, cannot create requests)
  if ((user?.isNation || user?.isEU) && !user?.isExcon && !isReadOnlyInstance) {
    actions.push({
      label: t('quickActions.newRequest'),
      path: '/requests/new',
      icon: <Plus size={16} />,
      variant: 'success',
      roles: ['NATION', 'EU'],
    });
  }

  // Continue drafts
  if (myDraftCount > 0) {
    actions.push({
      label: t('quickActions.myDrafts'),
      path: '/requests?status=DRAFT',
      icon: <FileEdit size={16} />,
      badge: myDraftCount,
      variant: 'warning',
      roles: ['NATION', 'EU', 'EXCON'],
    });
  }

  // EU - Pending reviews
  if (user?.isEU && !user?.isExcon && pendingReviewCount > 0) {
    actions.push({
      label: t('quickActions.pendingReview'),
      path: '/requests?status=UNDER_REVIEW',
      icon: <ClipboardCheck size={16} />,
      badge: pendingReviewCount,
      variant: 'warning',
      roles: ['EU'],
    });
  }

  // Active missions
  if (activeMissionCount > 0) {
    actions.push({
      label: t('quickActions.activeTransports'),
      path: '/missions',
      icon: <Target size={16} />,
      badge: activeMissionCount,
      variant: 'accent',
      roles: ['ALL'],
    });
  }

  // HUB - My Patients
  if (user?.isHub) {
    actions.push({
      label: t('quickActions.myPatients'),
      path: '/my-patients',
      icon: <Users size={16} />,
      variant: 'primary',
      roles: ['HUB'],
    });
  }

  if (actions.length === 0) return null;

  return (
    <div className="quick-actions">
      {actions.map(action => (
        <Link
          key={action.path + action.label}
          to={action.path}
          className={`quick-action-btn quick-action--${action.variant || 'primary'}`}
        >
          <span className={`quick-action-icon quick-action-icon--${action.variant || 'primary'}`}>
            {action.icon}
          </span>
          <span className="quick-action-label">{action.label}</span>
          {action.badge !== undefined && action.badge > 0 && (
            <span className="quick-action-badge">{action.badge}</span>
          )}
        </Link>
      ))}
    </div>
  );
}

export default QuickActions;
